import { ButtonDownloadLocaleConfig } from 'lib/components/buttondownload/ButtonDownload.vue.d';
import {
  Data,
  DataTableLocaleConfig,
  TableColumn,
} from 'lib/components/datatable/DataTable.vue.d';
import { Booleanish } from 'lib/components/ts-helpers';
import { LocaleConfig } from 'lib/plugins/WangsVue';

import { formatDate } from './date.util';
import { getNestedPropertyValue } from './object.util';
import loadSheetJSFromCDN from './xlsx.util';

export interface ITable {
  headers: string[];
  data: Record<string, unknown>[];
  tableName?: string;
  tableTitle?: string;
}

interface IExcelOptions {
  tables: ITable[];
  fileName: string;
  additionalTexts?: (string | string[])[];
}

export const exportToExcel = async (options: IExcelOptions): Promise<void> => {
  const XLSX = await loadSheetJSFromCDN();

  const workbook = XLSX.utils.book_new();
  const workSheet: any = XLSX.utils.json_to_sheet([]);

  let currentRow = 0;

  options.tables.forEach((table) => {
    if (table.tableTitle) {
      XLSX.utils.sheet_add_aoa(workSheet, [[table.tableTitle]], {
        origin: { r: currentRow, c: 0 },
      });
      currentRow += 1;
    }

    XLSX.utils.sheet_add_aoa(workSheet, [table.headers], {
      origin: { r: currentRow, c: 0 },
    });

    XLSX.utils.sheet_add_json(workSheet, table.data, {
      origin: { r: currentRow + 1, c: 0 },
      skipHeader: true,
    });

    currentRow += table.data.length + 3;
  });

  if (options.additionalTexts) {
    currentRow += 1;
    options.additionalTexts.forEach((each, index) => {
      if (Array.isArray(each)) {
        each.forEach((text, colIndex) => {
          XLSX.utils.sheet_add_aoa(workSheet, [[text]], {
            origin: { r: currentRow + index, c: colIndex },
          });
        });
      } else {
        XLSX.utils.sheet_add_aoa(workSheet, [[each]], {
          origin: { r: currentRow + index, c: 0 },
        });
      }
    });
  }

  XLSX.utils.book_append_sheet(workbook, workSheet, 'Sheet1');

  const timestamps = new Date()
    .toLocaleDateString('en-gb', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })
    .replaceAll('/', '');

  XLSX.writeFile(workbook, `${options.fileName}-${timestamps}.xlsx`);
};

export const normalizeTableData = <T extends Data = Data>(
  data?: T[],
  includedColumns?: TableColumn<T>[],
  locale?: (DataTableLocaleConfig | ButtonDownloadLocaleConfig) & {
    global: LocaleConfig['global'];
  },
): Record<string, unknown>[] => {
  return (data ?? []).map((item: T) => {
    const body = {} as Record<string, unknown>;

    includedColumns.forEach((col, index) => {
      const { field: colField, exportField, dateFormatOptions } = col;
      const field = exportField ?? colField; // Prioritize the exportField than colField.
      const propertyValue = getNestedPropertyValue(item, field);
      const fieldValue =
        propertyValue === undefined || propertyValue === ''
          ? (col.emptyText ?? '-') // Fallback to '-' if propertyValue is undefined or empty string
          : propertyValue;

      if (col.includeTruthyProperties) {
        const objectValue = (fieldValue ?? {}) as Record<string, Booleanish>;

        const truthyProperties = Object.keys(objectValue)
          .map((key) => {
            if (objectValue[key]) return key;
          })
          .filter(Boolean);

        body[field] = truthyProperties.join(',');
      } else if (Array.isArray(fieldValue)) {
        let arrayValue = fieldValue;

        if (col.arrayValueField) {
          /**
           * Support for export array data with deeply nested array.
           *
           * If the array is only string array, only return its value.
           */
          arrayValue = fieldValue.map((value) => {
            return (
              getNestedPropertyValue(value, col.arrayValueField)?.toString() ||
              col.emptyText ||
              '-' // Fallback to '-' if the value is undefined or empty string
            );
          });
        }

        body[field] = arrayValue.join(arrayValue.length > 1 ? ',' : ''); // Only join with comma if the length at least two item
      } else if (dateFormatOptions) {
        // If the data is date value, format using formatDate
        body[field] = formatDate(
          new Date(fieldValue as Date | string),
          dateFormatOptions,
        );
      } else if (col.booleanValue) {
        body[field] = fieldValue
          ? col.trueText || locale.trueText
          : col.falseText || locale.falseText;
      } else if (col.bodyTemplate) {
        // If the data has bodyTemplate, use it to format the value
        body[field] = col.bodyTemplate(item, index);
      } else {
        body[field] = fieldValue;
      }
    });

    return body;
  });
};
